/**
 * CompanyContext - Company Management Context
 * 
 * Provides company-related functionality:
 * - Current company information
 * - Company branding
 * - Company configuration
 * - Company statistics
 */

import { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const CompanyContext = createContext(null);

export function CompanyProvider({ children }) {
  const { companyId, isSuperAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load all companies (super admin only)
   */
  const loadAllCompanies = async () => {
    if (!isSuperAdmin()) return;
    
    setLoading(true);
    try {
      const companiesSnapshot = await getDocs(collection(db, 'companies'));
      const companiesList = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCompanies(companiesList);
      setError(null);
    } catch (err) {
      console.error('Error loading companies:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load specific company
   */
  const loadCompany = async (id) => {
    if (!id) return null;
    
    try {
      const companyDoc = await getDoc(doc(db, 'companies', id));
      if (companyDoc.exists()) {
        return { id: companyDoc.id, ...companyDoc.data() };
      }
      return null;
    } catch (err) {
      console.error('Error loading company:', err);
      return null;
    }
  };

  /**
   * Select a company (super admin can switch between companies)
   */
  const selectCompany = async (id) => {
    if (!id) {
      setSelectedCompany(null);
      return;
    }
    
    const company = await loadCompany(id);
    setSelectedCompany(company);
  };

  /**
   * Get company branding
   */
  const getBranding = (company = selectedCompany) => {
    if (!company) return null;
    return company.branding || {
      logo: '',
      primaryColor: '#4f6cf7',
      secondaryColor: '#2c356a',
      companyName: company.name
    };
  };

  /**
   * Get signature configuration
   */
  const getSignatureConfig = (company = selectedCompany) => {
    if (!company) return null;
    return company.signatureConfig || {
      x: 300,
      y: 700,
      width: 200,
      height: 100
    };
  };

  /**
   * Get company statistics
   */
  const getCompanyStats = async (id = companyId) => {
    if (!id) return null;
    
    try {
      // Count documents de la empresa
      const docsQuery = query(
        collection(db, 'requests'),
        where('companyId', '==', id)
      );
      const docsSnapshot = await getDocs(docsQuery);
      const totalDocs = docsSnapshot.size;
      const signedDocs = docsSnapshot.docs.filter(
        doc => doc.data().status === 'signed'
      ).length;
      
      console.log('Stats para companyId:', id, 'Documentos:', totalDocs, 'Firmados:', signedDocs);
      
      // Count users de la empresa
      const usersQuery = query(
        collection(db, 'users'),
        where('companyId', '==', id)
      );
      const usersSnapshot = await getDocs(usersQuery);
      const totalUsers = usersSnapshot.size;
      
      // Count contacts de la empresa
      const contactsQuery = query(
        collection(db, 'contacts'),
        where('companyId', '==', id)
      );
      const contactsSnapshot = await getDocs(contactsQuery);
      const totalContacts = contactsSnapshot.size;
      
      return {
        totalDocuments: totalDocs,
        signedDocuments: signedDocs,
        pendingDocuments: totalDocs - signedDocs,
        totalUsers,
        totalContacts
      };
    } catch (err) {
      console.error('Error getting company stats:', err);
      return null;
    }
  };

  /**
   * Check if company is active
   */
  const isCompanyActive = (company = selectedCompany) => {
    return company?.active === true;
  };

  /**
   * Get company type display name
   */
  const getCompanyTypeDisplay = (company = selectedCompany) => {
    if (!company) return '';
    
    const types = {
      'albaran': 'Albarán',
      'parte_trabajo': 'Parte de Trabajo',
      'custom': 'Personalizado'
    };
    
    return types[company.type] || company.type;
  };

  /**
   * Get subscription info
   */
  const getSubscription = (company = selectedCompany) => {
    if (!company) return null;
    return company.subscription || {
      plan: 'basic',
      documentsLimit: 100,
      usersLimit: 5,
      expiresAt: null
    };
  };

  /**
   * Check if company is within limits
   */
  const isWithinLimits = async (company = selectedCompany) => {
    if (!company) return false;
    
    const stats = await getCompanyStats(company.id);
    const subscription = getSubscription(company);
    
    if (!stats || !subscription) return false;
    
    return {
      documents: stats.totalDocuments < subscription.documentsLimit,
      users: stats.totalUsers < subscription.usersLimit,
      withinLimits: 
        stats.totalDocuments < subscription.documentsLimit &&
        stats.totalUsers < subscription.usersLimit
    };
  };

  // Load companies on mount if super admin
  useEffect(() => {
    if (isSuperAdmin()) {
      loadAllCompanies();
    }
  }, [isSuperAdmin]);

  // Auto-select company if user has one
  useEffect(() => {
    if (companyId && !selectedCompany) {
      selectCompany(companyId);
    }
  }, [companyId]);

  const value = {
    // State
    companies,
    selectedCompany,
    loading,
    error,
    
    // Methods
    loadAllCompanies,
    loadCompany,
    selectCompany,
    getCompanyStats,
    
    // Helpers
    getBranding,
    getSignatureConfig,
    isCompanyActive,
    getCompanyTypeDisplay,
    getSubscription,
    isWithinLimits,
    
    // Computed
    hasCompanies: companies.length > 0,
    currentCompanyId: selectedCompany?.id || companyId,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

/**
 * Hook to use company context
 */
export function useCompany() {
  const context = useContext(CompanyContext);
  
  if (!context) {
    throw new Error('useCompany must be used within CompanyProvider');
  }
  
  return context;
}

export default CompanyContext;
